import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Route,
  Redirect,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import { CssBaseline } from "@mui/material";
import { config } from "config";

import PrimaryTheme from "theme/primaryTheme";
import Snackbar from "components/common/Snackbar";
import JarvisDialog from "components/JarvisDialog";
import Loader from "components/common/Loader";

import CustomerNavigator from "navigator/CustomerNavigator";
import JarvisRMSNavigator from "navigator/JarvisRMSNavigator";
import JarvisPortfolioNavigator from "navigator/JarvisPortfolioNavigator";
import JarvisOneStockNavigator from "navigator/JarvisOneStockNavigator";
import KnowledgeCentreNavigator from "navigator/KnowledgeCentreNavigator";

import AppWelcomeScreen from "pages/AppWelcomeScreen";
import VerifyLink from "pages/VerifyLink";
import Login from "pages/Login";
import SignUp from "pages/SignUp";
import NotFound from "pages/NotFound";
import ForgotPassword from "pages/ForgotPassword";
import AppForceUpdate from "pages/AppForceUpdate";
import CampaignSSOLogin from "pages/CampaignSSOLogin";
import BrokerSSOLogin from "pages/BrokerSSOLogin";

import { PrivateRoute, PublicRoute } from "helper/route";
import PhcPrivateRoute from "navigator/PhcPrivateRoute";

import { commonActions } from "stores/common/commonActions";

import AppHOC from "AppHOC";

// storage
import { localStore } from "stores/localStorage";

// App
import { App } from "@capacitor/app";

// Lib
import { pushNotification } from "library/pushNotification";
import { branchio } from "library/branchio";
import { tracker } from "library/tracker";
// import { uxcam } from "library/tracker/uxcam";
import { Geolocation } from "@capacitor/geolocation";
import DeactiveAccountMessageDialog from "components/Auth/DeactiveAccountMessageDialog";
import { customerActions } from "stores/customer/customerActions";
import { intercom } from "library/tracker/intercom";
import ManualDocumentSigning from "pages/Customer/ManualDocumentSigning";
import ReKyc from "containers/ReKyc/ReKyc";
import JarvisPrimeNavigator from "navigator/JarvisPrimeNavigator";
import SentimentTrackerNavigator from "navigator/SentimentTrackerNavigator";
import Feedback from "containers/Feedback/Feedback";
import PhcNavigator from "navigator/PhcNavigator";
import PHCIframe from "pages/PHC/PHCIframe/PHCIframe";
import PageLoader from "components/common/PageLoader";
import AllInOne from "pages/AllInOne/AllInOne";

const PRODUCT_ROUTES = {
  [config.productCodes.PORTFOLIO]: "jarvis-portfolio",
  [config.productCodes.RMS]: "jarvis-rms",
  [config.productCodes.ONESTOCK]: "jarvis-onestock",
  [config.productCodes.PRIME]: "jarvis-prime",
  [config.productCodes.SENTIMENT]: "sentiment-tracker",
  [config.productCodes.PHC]: "phc",
};

const MainApp = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  // Store: customer
  const auth = useSelector((state) => state.customer);
  const deactiveAccountInfo = useSelector(
    (state) => state.customer.deactiveAccount,
  );

  // Store: Common
  const hasRiskProfiles = useSelector(
    (state) => state.common.riskProfiles.length > 0,
  );
  const deviceId = useSelector((state) => state.common.deviceId);
  const toastData = useSelector((state) => state.common.toast);
  const dialogData = useSelector((state) => state.common.dialog);
  const appConfig = useSelector((state) => state.common.appConfig);
  const globalLoader = useSelector(
    (state) => state.common.loading.globalLoader,
  );
  const isAppWelcomeScreenShown = useSelector(
    (state) => state.common.isAppWelcomeScreenShown,
  );

  // Store: onboarding completed?
  const isOnboardingCompleted = useSelector(
    (state) => state.onboarding.isOnboardingCompleted,
  );
  const RMSisOnboardingCompleted = useSelector(
    (state) => state.jarvisRMS.rmsOnboarding.isOnboardingCompleted,
  );
  const onestockOnboardingCompleted = useSelector(
    (state) => state.jarvisOnestock.onestockOnboarding.isOnboardingCompleted,
  );
  const isPrimeOnboardingCompleted = useSelector(
    (state) => state.jarvisPrime.primeOnboarding.isPrimeOnboardingCompleted,
  );

  const isSentimentTrackerOnboardingCompleted = useSelector(
    (state) =>
      state.sentimentTracker.sentimentTrackerOnboarding.isOnboardingCompleted,
  );
  const isPhcOnboardingCompleted = useSelector(
    (state) => state.PHC.PHCOnboarding.isOnboardingCompleted,
  );
  const sourceType = useSelector(
    (state) => state.onboarding.sourceType,
  );
  const isAllInOneSubscriptionActive = useSelector(
    (state) => state.onboarding.isAllInOneSubscriptionActive,
  );


  const anyOnboardingCompleted =
    isOnboardingCompleted ||
    RMSisOnboardingCompleted ||
    onestockOnboardingCompleted ||
    isPrimeOnboardingCompleted ||
    isSentimentTrackerOnboardingCompleted ||
    isPhcOnboardingCompleted;
  const isBroker =
    (auth && auth.loginProfile && auth.loginProfile.isBroker) || "";
  const isPhcBrokerUser = ["navia", "naive"].includes(
    String(isBroker).toLowerCase(),
  );

  const location = useLocation();
  let appDoubleBack = 0;
  let locationStateKey = "";

  const deviceIdRef = useRef(deviceId);
  React.useEffect(() => {
    deviceIdRef.current = deviceId;
  }, [deviceId]);

  // Re-register with APNs/FCM when the app returns to foreground so tokens stay
  // valid after the user enables notifications in system Settings.
  React.useEffect(() => {
    if (!config.isMobileApp) {
      return undefined;
    }

    let listener;
    const start = async () => {
      listener = await App.addListener("appStateChange", async ({ isActive }) => {
        if (!isActive) return;
        try {
          const token =
            await pushNotification.refreshPushRegistrationAfterResume();
          if (
            token &&
            !pushNotification.isPlaceholderDeviceId(token) &&
            token !== deviceIdRef.current
          ) {
            dispatch(commonActions.updateDeviceId(token));
          }
        } catch (e) {
          console.error("App resume: push token refresh failed", e);
        }
      });
    };

    start();

    return () => {
      listener?.remove?.();
    };
  }, [dispatch]);

  // Effect 1: On didMount
  React.useEffect(() => {
    // Check if dev environment
    if (config.environment === "DEVELOPMENT" && config.isMobileApp) {
      // alert("This is for test app");
    }
    if (!hasRiskProfiles) {
      dispatch(commonActions.getRiskProfiles());
    }

    // ask once when app opens
    pushNotification.init();
    branchio.init();
    appBackClick(); // App: Init event to handle double back click exit
    // uxcam.startSession();
    getCurrentLocation(); // Set current location to local storage.
    changeProductOnPath(); // Change Active product based on initial path

    return () => {
      tracker.unmountApp();
    };
  }, []);

  // Effect 2: On Path Change
  React.useEffect(() => {
    if (auth.isAuthenticated) {
      redirectIfAnyOnboardingCompleted();
    }
    redirectIfForceUpdate();
  }, [location.pathname]);

  // Effect 2.5: Redirect inactive All-in-One users from product hub page
  React.useEffect(() => {
    if (
      auth.isAuthenticated &&
      (location.pathname === "/customer/product-info" ||
        location.pathname === "/customer/dashboard") &&
      sourceType &&
      String(sourceType).toUpperCase() === "ALL_IN_ONE" &&
      (isAllInOneSubscriptionActive === 0 ||
        isAllInOneSubscriptionActive === false ||
        isAllInOneSubscriptionActive === "0" ||
        isAllInOneSubscriptionActive === null)
    ) {
      history.push("/all-in-one");
    }
  }, [location.pathname, auth.isAuthenticated, sourceType, isAllInOneSubscriptionActive, history]);

  // Effect 3: On Auth Change
  React.useEffect(() => {
    tracker.init({
      isLoggedIn: auth.isAuthenticated,
      loginProfile: auth.loginProfile,
      deviceId: deviceId,
    });

    if (auth.isAuthenticated) {
      tracker.initTracker();
    }

    const getAppConfig = async () => {
      if (auth.isAuthenticated && config.isMobileApp) {
        const { version } = await App.getInfo();
        const operatingSystem = config.platform;
        dispatch(
          commonActions.getAppConfig({
            appVersion: version,
            operatingSystem,
          }),
        );
      }
    };
    getAppConfig();
  }, [auth.isAuthenticated, auth.loginProfile, deviceId, dispatch]);

  // Effect: resolve push token for Redux when missing or still a local placeholder.
  React.useEffect(() => {
    const updateDeviceId = async () => {
      if (deviceId == null) {
        await pushNotification.getToken((token) =>
          dispatch(commonActions.updateDeviceId(token)),
        );
        return;
      }
      if (pushNotification.isPlaceholderDeviceId(deviceId)) {
        await pushNotification.getToken((token) => {
          if (!pushNotification.isPlaceholderDeviceId(token)) {
            dispatch(commonActions.updateDeviceId(token));
          }
        });
      }
    };

    updateDeviceId();
  }, [deviceId, dispatch]);

  // Redirect feedback redirect logic
  React.useEffect(() => {
    if (!auth.isAuthenticated || !auth.profile) return;

    const isReviewCount = auth.profile.reviewCount; // adjust your field name

    if (isReviewCount > 0) {
      const lastRedirect = localStorage.getItem("feedbackRedirectDate");
      const today = new Date().toDateString();

      if (lastRedirect !== today) {
        localStorage.setItem("feedbackRedirectDate", today);
        window.location.replace("/feedback");
      }
    }
  }, [auth.profile]);

  // change product path in local storage based on first URL hit.
  const changeProductOnPath = () => {
    // set Active product based on direct path.
    const urlPath = location.pathname;
    Object.keys(PRODUCT_ROUTES).some((p) => {
      if (urlPath.search(PRODUCT_ROUTES[p]) !== -1) {
        localStore.setActiveProduct(p);
        return true;
      }
      return false;
    });
  };

  // Redirect if force update
  const redirectIfForceUpdate = () => {
    if (auth.isAuthenticated && config.isMobileApp && appConfig.forceUpdate) {
      history.push("/appForceUpdate");
    }
  };

  // Redorect if onboarding completed
  const redirectIfAnyOnboardingCompleted = () => {
    const urlPath = location.pathname;
    const restrictUrls = ["/customer/product-info", "/customer/dashboard"];
    let redirectPath = "";

    // new code to redirect if any onboarding completed
    if (anyOnboardingCompleted) {
      if (restrictUrls.includes(urlPath) && urlPath === "/customer/product-info") {
        redirectPath = "/customer/dashboard";
      }
    }

    // old code to redirect if any onboarding completed

    // if (anyOnboardingCompleted) {
    //   if (restrictUrls.includes(urlPath)) {
    //     const activeProduct = localStore.getActiveProduct();
    //     if (activeProduct && PRODUCT_ROUTES[activeProduct]) {
    //       redirectPath = `/${PRODUCT_ROUTES[activeProduct]}/dashboard`;
    //     } else if (isOnboardingCompleted) {
    //       redirectPath = "/jarvis-portfolio/dashboard";
    //     } else if (RMSisOnboardingCompleted) {
    //       redirectPath = "/jarvis-rms/dashboard";
    //     } else if (onestockOnboardingCompleted) {
    //       redirectPath = "/jarvis-onestock/dashboard";
    //     } else if (isPrimeOnboardingCompleted) {
    //       redirectPath = "/jarvis-prime/dashboard";
    //     } else if (isSentimentTrackerOnboardingCompleted) {
    //       redirectPath = "/sentiment-tracker/dashboard";
    //     } else if (isPhcOnboardingCompleted) {
    //       redirectPath = "/phc/dashboard";
    //     }
    //   }
    // }

    if (auth.isAuthenticated && redirectPath) {
      history.push(redirectPath);
    }
  };
  // App : Handle double click exit.
  const appBackClick = () => {
    if (!config.isMobileApp) {
      return false;
    }

    App.addListener("backButton", (e) => {
      if (appDoubleBack) {
        let confirm = window.confirm("Do you want to exit the app?");
        if (confirm) {
          App.exitApp();
        }
      } else {
        window.history.back();
        if (
          window.history.state &&
          window.history.state.key === locationStateKey
        ) {
          let confirm = window.confirm("Do you want to exit the app?");
          if (confirm) {
            App.exitApp();
          }
        }
        locationStateKey = window.history.state.key;
      }

      appDoubleBack = 1;
      setTimeout(() => {
        appDoubleBack = 0;
      }, 350);
    });
  };

  const getInitialPath = () => {
    let path = "/login";
    if (!isAppWelcomeScreenShown && config.isMobileApp) {
      path = "/appWelcome";
    }
    return path;
  };

  //** Set publicRedirectTo path
  const getPublicRedirectTo = () => {
    // default path
    let publicRedirectTo = "/customer/profile";

    // Set Active product through URL params
    const urlParams = new URLSearchParams(window.location.search);
    const productType = urlParams.get("product");
    if (productType) {
      localStore.setActiveProduct(productType);
    }

    if (!auth.isAuthenticated) {
      return "/";
    }

    // get active product
    const activeProduct = localStore.getActiveProduct();

    // Jarvis Info Page for new customer.
    const seenJarvisInfoPage = localStore.getSeenJarvisInfo(); // undefined | 0 | 1

    if (!activeProduct && !seenJarvisInfoPage) {
      if (!anyOnboardingCompleted && isPhcBrokerUser) {
        return "/phc/onboarding/portfolio-upload";
      }
      // old code to redirect if any onboarding completed
      // return "/customer/product-info";
     // new code to redirect if any onboarding completed
      return "/customer/dashboard";
    }

    // Active product path & whether that specific product is already onboarded
    let productPath = "";
    let isActiveProductOnboarded = false;
    if (activeProduct) {
      switch (activeProduct.toUpperCase()) {
        case config.productCodes.PORTFOLIO:
          productPath = "/jarvis-portfolio";
          isActiveProductOnboarded = isOnboardingCompleted;
          break;
        case config.productCodes.RMS:
          productPath = "/jarvis-rms";
          isActiveProductOnboarded = RMSisOnboardingCompleted;
          break;
        case config.productCodes.ONESTOCK:
          productPath = "/jarvis-onestock";
          isActiveProductOnboarded = onestockOnboardingCompleted;
          break;
        case config.productCodes.PRIME:
          productPath = "/jarvis-prime";
          isActiveProductOnboarded = isPrimeOnboardingCompleted;
          break;
        case config.productCodes.SENTIMENT:
          productPath = "/sentiment-tracker";
          isActiveProductOnboarded = isSentimentTrackerOnboardingCompleted;
          break;
        case config.productCodes.PHC:
          productPath = "/phc";
          isActiveProductOnboarded = isPhcOnboardingCompleted;
          break;
        default:
      }
    }

    // Redirect Case 1: has an active product (e.g. ?product=portfolio / ?product=onestock)
    // that hasn't been onboarded yet — always send to that product's onboarding overview,
    // even if the user has already onboarded a different product.
    if (activeProduct && productPath && !isActiveProductOnboarded) {
      publicRedirectTo = `${productPath}/onboarding/overview`;
    }
    // Select based on any onboarding completed — land on unified hub.
    else if (anyOnboardingCompleted) {
      publicRedirectTo = "/customer/dashboard";
    }

    // old code to redirect if any onboarding completed
    // if (anyOnboardingCompleted) {
    //   if (isOnboardingCompleted) {
    //     publicRedirectTo = `/jarvis-portfolio/dashboard`;
    //   } else if (RMSisOnboardingCompleted) {
    //     publicRedirectTo = `/jarvis-rms/dashboard`;
    //   } else if (onestockOnboardingCompleted) {
    //     publicRedirectTo = `/jarvis-onestock/dashboard`;
    //   } else if (isPrimeOnboardingCompleted) {
    //     publicRedirectTo = `/jarvis-prime/dashboard`;
    //   } else if (isSentimentTrackerOnboardingCompleted) {
    //     publicRedirectTo = `/sentiment-tracker/dashboard`;
    //   } else if (isPhcOnboardingCompleted) {
    //     publicRedirectTo = `/phc/dashboard`;
    //   } else if (activeProduct && productPath) {
    //     publicRedirectTo = `${productPath}/dashboard`;
    //   }
    // }
    else {
      // Redirect Case 2: no active product & first visit
      if (!activeProduct && !seenJarvisInfoPage) {
        // publicRedirectTo = "/customer/jarvis-info";              //NOTE: now onwards this page is ignore.
        publicRedirectTo =
          !anyOnboardingCompleted && isPhcBrokerUser
            ? "/phc/onboarding/portfolio-upload"
            : "/customer/dashboard"; // new code to redirect if any onboarding completed
        localStore.setSeenJarvisInfo(1);
      }

      // Redirect Case 3: no active product & no first visit
      if (!activeProduct && seenJarvisInfoPage) {
        publicRedirectTo =
          !anyOnboardingCompleted && isPhcBrokerUser
            ? "/phc/onboarding/portfolio-upload"
            : "/customer/dashboard";
      }
    }

    return publicRedirectTo;
  };
  const publicRedirectTo = getPublicRedirectTo();

  const getCurrentLocation = async () => {
    try {
      const gl = await Geolocation.getCurrentPosition();
      if (gl) {
        localStore.setGeolocation({
          latitude: gl.coords.latitude,
          longitude: gl.coords.longitude,
        });
      }
    } catch (error) {
      // Location can be unavailable/denied on web or device settings — ignore.
    }
  };

  const handleDeactiveAccountMessageClose = () => {
    dispatch(customerActions.resetDeactiveAccountData());
  };

  const handleDialogClose = () => {
    dispatch(commonActions.resetDialog());
  };
  const handleDialogConfirm = () => {
    if (dialogData && dialogData.onCloseTakeToIntercom) {
      intercom.show();
    }
    dispatch(commonActions.resetDialog());
  };

  return (
    <div
      style={{
        paddingTop: `var(--safe-area-inset-top)`,
        paddingBottom: `var(--safe-area-inset-bottom)`,
        paddingLeft: `var(--safe-area-inset-left)`,
        paddingRight: `var(--safe-area-inset-right)`,
        position: "relative",
      }}
    >
      {
        <div
          style={{
            height: `var(--safe-area-inset-top)`,
            backgroundColor: "#2B70C5",
            position: "fixed",
            zIndex: 99999,
            top: 0,
            left: 0,
            width: "100%",
          }}
        ></div>
      }
      <AppHOC>
        <PrimaryTheme>
          <Loader position="fixed" size="lg" loading={globalLoader} />
          <CssBaseline />

          <JarvisDialog
            disableCloseIcon
            open={dialogData.open}
            onClose={handleDialogClose}
            onConfirm={handleDialogConfirm}
            title={dialogData.heading}
            content={dialogData.message}
          />

          <Snackbar
            open={toastData.open}
            onClose={() => dispatch(commonActions.resetToast())}
            message={toastData.message}
            autoHideDuration={toastData.timeout}
          />
          <DeactiveAccountMessageDialog
            isOpen={deactiveAccountInfo.isDialogOpen}
            onClose={handleDeactiveAccountMessageClose}
            message={deactiveAccountInfo.message}
            email={deactiveAccountInfo.email}
          />
          <Switch>
            <Route
              exact
              path="/sso/:authkey"
              component={CampaignSSOLogin}
              auth={auth}
            />
            <Route
              exact
              path="/broker-sso"
              component={BrokerSSOLogin}
              auth={auth}
            />
            <PublicRoute
              exact
              path="/login"
              component={Login}
              auth={auth}
              redirectTo={publicRedirectTo}
            />
            <Route
              exact
              path="/verify-email"
              component={VerifyLink}
              auth={auth}
            />
            <PublicRoute
              exact
              path="/appWelcome"
              component={AppWelcomeScreen}
              auth={auth}
              redirectTo={publicRedirectTo}
            />
            <PublicRoute
              exact
              path="/signup"
              component={SignUp}
              auth={auth}
              redirectTo={publicRedirectTo}
            />
            <PublicRoute
              exact
              path="/forgot-password"
              component={ForgotPassword}
              auth={auth}
              redirectTo={publicRedirectTo}
            />

            <Route exact path={`/appForceUpdate`} component={AppForceUpdate} />

            {/* customer details related navigators */}
            <PrivateRoute
              path="/customer"
              auth={auth}
              component={CustomerNavigator}
            />

            {/* product jarvis portfolio (advisory) related navigators */}
            <PrivateRoute
              path="/jarvis-portfolio"
              auth={auth}
              component={JarvisPortfolioNavigator}
            />

            {/* product jarvis prime (advisory) related navigators */}
            <PrivateRoute
              path="/jarvis-prime"
              auth={auth}
              component={JarvisPrimeNavigator}
            />

            {/* product jarvis portfolio (advisory) related navigators */}
            <PrivateRoute
              path="/manual-document-signing"
              auth={auth}
              component={ManualDocumentSigning}
            />

            {/* product jarvis rms (protect) related navigators */}
            <PrivateRoute
              path="/jarvis-rms"
              auth={auth}
              component={JarvisRMSNavigator}
            />

            {/* product jarvis onestock related navigators */}
            <PrivateRoute
              path="/jarvis-onestock"
              auth={auth}
              component={JarvisOneStockNavigator}
            />

            {/* product sentiment tracker related navigators */}
            <PrivateRoute
              path="/sentiment-tracker"
              auth={auth}
              component={SentimentTrackerNavigator}
            />

            {/* product PHC  related navigators (preview links may carry token before session) */}
            <PhcPrivateRoute
              path="/phc"
              auth={auth}
              component={PhcNavigator}
              redirectTo="/login"
            />

            {/* knowledge centre related navigators */}
            <PrivateRoute
              path="/knowledge-centre"
              auth={auth}
              component={KnowledgeCentreNavigator}
            />

            {/* phc for ifram */}
            <Route exact path="/:token/:id" component={PHCIframe} />
            <Route exact path="/:token/:id/*" component={PHCIframe} />

            {/* Re-Kyc related navigators */}
            <Route exact path="/re-kyc" component={ReKyc} />

            <PrivateRoute
              exact
              path="/all-in-one"
              component={AllInOne}
              auth={auth}
            />
            {/* <Route exact path="/all-in-one" component={AllInOne} auth={auth} /> */}

            <Route path="/loading" component={PageLoader} />

            {/* Feedback  */}
            <PrivateRoute path="/feedback" auth={auth} component={Feedback} />
            <Redirect exact from="/" to={getInitialPath()} />
            <Route component={NotFound} />
          </Switch>
        </PrimaryTheme>
      </AppHOC>
    </div>
  );
};

// disable consoles for non debug mode
// if (!config.debug) {
//   if (!window.console) window.console = {};
//   var methods = ["log", "debug", "info", "table"];
//   for (var i = 0; i < methods.length; i++) {
//     console[methods[i]] = function () {};
//   }
// }

export default React.memo(MainApp);
